import { useEffect, useMemo } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { Field } from '../ui/Field';
import { useAdmin } from '../AdminContext';
import { useCan } from '../useCan';
import { inputCls } from '../ui/styles';
import { ADMIN_STEP_MINUTES, DEFAULT_STAFF_ID, conflictsFor, endTime, freeTimes, outsideHours } from '../../core/rules/agenda';
import { salonDayFor } from '../../core/rules/salonDays';
import { isIsoDate, today } from '../../core/format/dates';
import { blockLabel } from './agendaRules';
import type { AgendaAppointment, AgendaBlock } from '../../core/types/agenda';
type Props = {
  date: string;
  onDate: (date: string) => void;
  time: string;
  onTime: (time: string) => void;
  durationMinutes: number;
  appointments: AgendaAppointment[];
  blocks: AgendaBlock[];
  /** De afspraak die verzet wordt: botst niet met zichzelf. */
  ignoreId?: string;
  /** Bewust over iets heen plannen (tussendoortje, vriendin die even komt). */
  overlap: boolean;
  onOverlap: (on: boolean) => void;
};

/**
 * Dag en tijd voor Sarah. Standaard alleen vrije tijden, per kwartier, met 15 minuten
 * opruimtijd na elke afspraak. "Toch op een andere tijd" laat elke tijd toe en zegt
 * eerlijk waarmee hij botst; de server krijgt dan `allowOverlap` mee.
 */
export function TimeChoice({
  date,
  onDate,
  time,
  onTime,
  durationMinutes,
  appointments,
  blocks,
  ignoreId,
  overlap,
  onOverlap
}: Props) {
  const {
    site
  } = useAdmin();
  // Over iets heen of buiten de tijden plannen mag alleen met `agenda.override` (de server weigert
  // het anders): zonder die bevoegdheid staat de keuze er niet.
  const mayOverride = useCan().canAll('agenda.override');
  const free = useMemo(() => isIsoDate(date) ? freeTimes(site.openingHours, date, durationMinutes, appointments, blocks, {
    step: ADMIN_STEP_MINUTES,
    ignoreId,
    exceptions: site.salonExceptions
  }) : [], [site.openingHours, site.salonExceptions, date, durationMinutes, appointments, blocks, ignoreId]);
  const staffId = isIsoDate(date) && salonDayFor(site.openingHours, site.salonExceptions, date)?.staffId || DEFAULT_STAFF_ID;
  // Zonder "toch op een andere tijd" mag alleen een vrije tijd gekozen blijven: anders zou een
  // oude (bezette) tijd verstuurd worden terwijl de keuzelijst leeg lijkt.
  useEffect(() => {
    if (!overlap && time && !free.includes(time)) onTime('');
  }, [overlap, time, free, onTime]);
  const span = {
    date,
    time,
    durationMinutes,
    staffId
  };
  const conflicts = overlap && time ? conflictsFor(span, appointments, blocks, {
    ignoreId
  }) : [];
  const outside = overlap && time && isIsoDate(date) ? outsideHours(site.openingHours, span, site.salonExceptions) : false;
  return <div className="space-y-3" data-id="element-517" data-mp="1duuroos9ag7a">
      <div className="grid gap-3 sm:grid-cols-2" data-id="element-518" data-mp="1dvmp855eyo07">
        <Field label="Datum" data-id="element-519" data-mp="1dsqm82epl94w">
          <input type="date" value={date} min={today()} onChange={e => onDate(e.target.value)} className={inputCls} data-id="element-520" data-mp="3kk9dbs46gbbn" />
        </Field>
        <Field label="Tijd" hint={time ? `Tot ${endTime({
        time,
        durationMinutes
      })}` : undefined} data-id="element-521" data-mp="1v3nl3r07oess">
          {overlap ? <input type="time" step={ADMIN_STEP_MINUTES * 60} value={time} onChange={e => onTime(e.target.value)} className={inputCls} data-id="element-522" data-mp="202h05nv5cdv2" /> : <select value={free.includes(time) ? time : ''} onChange={e => onTime(e.target.value)} className={inputCls} disabled={free.length === 0} data-id="element-523" data-mp="1qcxde74bbzux">
              <option value="" data-id="element-524" data-mp="1cc4hpj29yyq8">{free.length ? 'Kies een vrije tijd' : 'Geen vrije tijd op deze dag'}</option>
              {free.map(t => <option key={t} value={t} data-id="element-525" data-mp="12fi2b9spwzvl">
                  {t}
                </option>)}
            </select>}
        </Field>
      </div>

      {mayOverride && <label className="flex cursor-pointer items-start gap-2.5 text-[13px] text-ink" data-id="element-526" data-mp="2wt9f2i6cfzv9">
          <input type="checkbox" checked={overlap} onChange={e => onOverlap(e.target.checked)} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-527" data-mp="31scbxfc66zs5" />
          <span data-id="element-528" data-mp="11qcpz9qpyuc9">
            Toch op een andere tijd
            <span className="block text-[12px] text-muted" data-id="element-529" data-mp="11nqkdfne2owo">Ook buiten openingstijden of over iets anders heen.</span>
          </span>
        </label>}

      {(conflicts.length > 0 || outside) && <div role="status" className="flex gap-2 rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" data-id="element-530" data-mp="sr9643jqaawp">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-531" data-mp="ir8oqn5f64v5" />
          <div data-id="element-532" data-mp="e00a9cp8ftb4">
            {outside && <p data-id="element-533" data-mp="dzqlttju5u7j">Valt (deels) buiten de openingstijden.</p>}
            {conflicts.map(c => <p key={c.item.id} data-id="element-534" data-mp="928lgrjvwnb5">
                Botst met {c.kind === 'appointment' ? `${c.item.customer.name} (${c.item.time})` : `${blockLabel(c.item.reason, c.item.label).toLowerCase()} (${c.item.time})`}
                {c.kind === 'appointment' && ', inclusief een kwartier opruimen'}.
              </p>)}
          </div>
        </div>}
    </div>;
}